import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useState, useEffect, useCallback, Children, cloneElement } from 'react';
import styled, { css } from 'styled-components';
import { g as getTranslationComponent } from '../../i18n/SummaryCardRowTranslation/index-D5oLHOIL.js';
import { S as ScrollButton, a as StyledScrollButton } from './ScrollButton-BmqIvrgl.js';
import { E as EXPANDED_EXIT_TRANSITION_DURATION, s as scrollToChild, h as hasLeftOverflow, a as hasRightOverflow } from './utils-Bch8lY8w.js';
import { E as ExpandedCard } from '../ExpandedCard/ExpandedCard-DEp359ID.js';
import { u as useIsMobile } from '../../theme/MobileThemeProvider/useIsMobile-B-VP6bHU.js';
import { M as MobilePagination } from './MobilePagination-BydrGtmS.js';
import { i as isRtlEnabled } from '../../LocalModules/storm-ui-utils/dist/esm/isRtlEnabled-DXSQ1gyT-C6zs_USs.js';
import { I as IntlProvider } from '../../LocalModules/storm-ui-intl/dist/esm/IntlProvider-DUNZ897o-Dn-1_05J.js';
import { T as TaktIdProvider } from '../../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';

const SummaryCardRowContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40025937__sc-ky1hzd-0"
})(["position:relative;display:flex;flex-direction:column;align-items:center;", " :hover{", "{opacity:1;}}"], ({
  $expanded
}) => $expanded && css(["min-height:", "px;height:auto;"], ({
  $minHeight
}) => ($minHeight !== null && $minHeight !== void 0 ? $minHeight : 0) + 2), StyledScrollButton);
const SummaryCardScrollContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40025937__sc-ky1hzd-1"
})(["width:100%;position:relative;overflow-x:auto;display:flex;box-shadow:", ";border:", ";border-radius:", ";", ""], ({
  theme
}) => theme.elevation.shadow.card, ({
  theme
}) => theme.border.subtle.default, ({
  theme
}) => theme.border.radius.large, ({
  $expanded
}) => $expanded && css(["min-height:", "px;position:absolute;overflow-x:hidden;top:50%;transform:translateY(-50%);"], ({
  $minHeight
}) => $minHeight !== null && $minHeight !== void 0 ? $minHeight : 0));
const SmallCards = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40025937__sc-ky1hzd-2"
})(["display:inline-flex;flex-grow:1;flex-direction:row;& > :not(:last-child):not(:only-child){border-inline-end:", ";}"], ({
  theme
}) => theme.border.subtle.default);
const SummaryCardRow = ({
  rowName,
  expandedCardName: expandedCardNameProp,
  onExpandToggle,
  children,
  // taktProps
  taktId,
  taktValue,
  // intl props
  locale = 'en-US',
  onError,
  onReset,
  fallback,
  ...rest
}) => {
  const isMobile = useIsMobile();
  const isRtl = isRtlEnabled();
  const scrollRowRef = useRef(null);
  const smallCardsRef = useRef(null);
  const expandedCardRef = useRef(null);
  const expandedCardPropsRef = useRef();
  const childrenRef = useRef(new Map());
  const [expandedState, setExpandedState] = useState('collapsed');
  const expandTransitionTimeoutRef = useRef();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [scrolledToCardIndex, setScrolledToCardIndex] = useState(0);
  const [height, setHeight] = useState(undefined);
  const translations = getTranslationComponent(locale);
  useEffect(() => {
    childrenRef.current.clear();
  }, [children]);
  const updateExpandedCardStates = useCallback(({
    cardProps
  }) => {
    if (cardProps && cardProps.expandedContent) {
      expandedCardPropsRef.current = cardProps;
      setExpandedState('expanded');
    } else {
      if (expandTransitionTimeoutRef.current) {
        clearTimeout(expandTransitionTimeoutRef.current);
      }
      setExpandedState('transition');
      expandTransitionTimeoutRef.current = setTimeout(() => {
        setExpandedState('collapsed');
        expandedCardPropsRef.current = undefined;
      }, EXPANDED_EXIT_TRANSITION_DURATION);
    }
  }, []);
  useEffect(() => {
    const expandCard = Children.toArray(children)
    // get the first child with the matching cardName prop
    .filter(child => child.props.cardName === expandedCardNameProp)[0];
    updateExpandedCardStates({
      cardProps: expandCard === null || expandCard === void 0 ? void 0 : expandCard.props
    });
  }, [children, expandedCardNameProp, updateExpandedCardStates]);
  const handleSetExpandedCard = ({
    event,
    cardProps
  }) => {
    updateExpandedCardStates({
      cardProps
    });
    onExpandToggle === null || onExpandToggle === void 0 || onExpandToggle(cardProps === null || cardProps === void 0 ? void 0 : cardProps.cardName, event);
  };
  const checkOverflow = useCallback(() => {
    const canScrollLeft = hasLeftOverflow(scrollRowRef.current);
    const canScrollRight = hasRightOverflow(scrollRowRef.current);
    if (!isRtl && canScrollPrev !== canScrollLeft || isRtl && canScrollPrev !== canScrollRight) {
      setCanScrollPrev(!canScrollPrev);
    }
    if (!isRtl && canScrollNext !== canScrollRight || isRtl && canScrollNext !== canScrollLeft) {
      setCanScrollNext(!canScrollNext);
    }
  }, [canScrollPrev, canScrollNext, isRtl, scrollRowRef]);

  // check if we should show/hide the scroll arrow buttons
  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [scrollRowRef, checkOverflow]);

  // get the updated height of the container if the card content changes
  // this allows us to transition without jumping around in size
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      var _smallCardsRef$curren;
      setHeight((_smallCardsRef$curren = smallCardsRef.current) === null || _smallCardsRef$curren === void 0 ? void 0 : _smallCardsRef$curren.offsetHeight);
    });
    if (smallCardsRef.current) {
      resizeObserver.observe(smallCardsRef === null || smallCardsRef === void 0 ? void 0 : smallCardsRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [smallCardsRef]);
  const scrollLeft = useCallback(() => {
    const childIndex = scrollToChild({
      scrollRowRef,
      childNodes: Array.from(childrenRef.current.values()).map(element => element),
      direction: 'left'
    });
    setScrolledToCardIndex(childIndex);
  }, [scrollRowRef, childrenRef]);
  const scrollRight = useCallback(() => {
    const childIndex = scrollToChild({
      scrollRowRef,
      childNodes: Array.from(childrenRef.current.values()).map(element => element),
      direction: 'right'
    });
    setScrolledToCardIndex(childIndex);
  }, [scrollRowRef, childrenRef]);
  return /*#__PURE__*/React__default.createElement(IntlProvider, {
    onError: onError,
    resetKey: locale,
    fallback: fallback,
    onReset: onReset,
    translations: translations
  }, /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('summary-card-row')
  }, /*#__PURE__*/React__default.createElement("div", rest, /*#__PURE__*/React__default.createElement(SummaryCardRowContainer, {
    $expanded: expandedState !== 'collapsed',
    $minHeight: expandedState !== 'collapsed' ? height : 0
  }, !isMobile && expandedState === 'collapsed' && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, canScrollPrev && /*#__PURE__*/React__default.createElement(ScrollButton, {
    direction: "left",
    onClick: scrollLeft
  }), canScrollNext && /*#__PURE__*/React__default.createElement(ScrollButton, {
    direction: "right",
    onClick: scrollRight
  })), /*#__PURE__*/React__default.createElement(SummaryCardScrollContainer, {
    ref: scrollRowRef,
    onScroll: checkOverflow,
    $expanded: expandedState !== 'collapsed',
    $minHeight: expandedCardPropsRef.current ? height : 0
  }, /*#__PURE__*/React__default.createElement(SmallCards, _extends({
    $expanded: expandedState !== 'collapsed',
    ref: smallCardsRef
  }, expandedState !== 'collapsed' ? {
    inert: 'true'
  } : {}), Children.map(children, (child, index) => (/*#__PURE__*/cloneElement(child, {
    ref: ref => {
      childrenRef.current.set(child.props.cardName, ref);
    },
    _setExpandedCard: handleSetExpandedCard,
    _rowName: rowName
  }))))), expandedCardPropsRef.current && /*#__PURE__*/React__default.createElement(ExpandedCard, {
    expandedCardProps: expandedCardPropsRef.current,
    ref: expandedCardRef,
    maxHeight: height,
    setExpandedCard: handleSetExpandedCard,
    expandedState: expandedState
  })), isMobile && /*#__PURE__*/React__default.createElement(MobilePagination, {
    canScrollNext: canScrollNext,
    canScrollPrev: canScrollPrev,
    onScrollLeft: scrollLeft,
    onScrollRight: scrollRight,
    disabled: expandedState !== 'collapsed',
    scrolledToCard: scrolledToCardIndex + 1,
    cardCount: Array.isArray(children) ? children.length : [children].length
  }))));
};

export { SummaryCardRow as S };
//# sourceMappingURL=SummaryCardRow-QSVv8Xr2.js.map
